import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import {getTarget} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import * as style from "@COMPONENTS/style/productlist/productlist";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {OptionTextField, OptionTitle, OptionWrap, Search, SearchWrap} from "@FRAMEWORK/ui/desktop/filter";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {SelectBox, SelectLabel, SelectOptions, SelectWrap} from "@FRAMEWORK/ui/desktop/select";
import errorImg from "@PUBLIC/P/images/ico/ico_nodata.png";
import useBannerContents, {BannerContents} from "@QUERIES/essential/banner/useBannerContents";
import {GoodsFilter} from "@QUERIES/prdt/goods/useGoodsFilter";
import {format} from "@yb/utils";
import dayjs, {Dayjs} from "dayjs";
import React, {SyntheticEvent, useEffect, useMemo, useRef, useState} from "react";
import useFilterStore from "../../common/filterHook";
import LocalFilterCalendar from "./LocalFilterCalendar";

type FilterType = GoodsFilter & {
	active: number | null;
};

/* 출발일정 */
const StartDate = (prop: FilterType) => {
	const {getFilter, setFilter, removeFilter} = useFilterStore();
	const [popOpen] = useOverlay();
	const monList = prop.monList?.replaceAll("|", "").split("^");
	const [minDate, setMinDate] = useState<string>();
	const [maxDate, setMaxDate] = useState<string>();

	const [show, setShow] = useState<boolean>(false);
	const [monValue, setMonValue] = useState<string>();
	const outStartDateValue = getFilter()?.filter(i => i.key === "outStartDateList")[0]?.value || "";
	/* 달력, 기본값 초기화 */
	useEffect(() => {
		/* 월달력 */
		if (outStartDateValue.length === 6) {
			const storeMonValue = dayjs(outStartDateValue).format("YYYY년MM월") || "출발월 전체";
			setMonValue(storeMonValue);
		} else if (monList) {
			const [firstMon, lastMon] = [monList[0], monList[monList.length - 1]];
			setMinDate(firstMon);
			setMaxDate(lastMon);
			setMonValue("출발월 전체");
		}
	}, [prop, monList, outStartDateValue]);

	const handleConfirmAndSetFilter = (key: string, value: string, text: string) => {
		const setArr = getFilter()?.filter(
			v => v.key !== "outStartDateList" && (value.length === 6 || v.key !== "wdayList"),
		);
		const afterConfirm = () => {
			setArr.push({key, value, text});
			setFilter(setArr);
			return false;
		};

		popOpen(props => (
			<Alert
				{...props}
				alertType="confirm"
				text="출발 일정 조건을 변경하시는 경우<br/>기존 설정하신 출발 일정은 초기화 됩니다.<br/>출발 일정 조건을 변경하시겠습니까?"
				afterConfirm={() => typeof afterConfirm === "function" && afterConfirm()}
			/>
		));
		return false;
	};

	const handleMonList = e => {
		const value = e.target.getAttribute("value");
		const text = dayjs(value.toString()).format("YYYY년MM월 출발");
		const setArr = getFilter()?.filter(v => v.key !== "outStartDateList");
		setShow(false);
		if (outStartDateValue.length === 8) {
			return handleConfirmAndSetFilter("outStartDateList", value.toString(), text);
		}
		if (value === "ALL") {
			removeFilter("outStartDateList");
		} else {
			setArr.push({key: "outStartDateList", value: value.toString(), text});
			setFilter(setArr);
		}
	};

	const handleCalendar = (date: string) => {
		const setArr = getFilter()?.filter(v => v.key !== "outStartDateList" && v.key !== "wdayList");
		const text = `${dayjs(date).format("YYYY.MM.DD")} 출발`;

		const wday = getFilter()?.filter(i => i.key === "wdayList")[0]?.value || "";
		if (outStartDateValue.length === 6 || wday.length > 0) {
			return handleConfirmAndSetFilter("outStartDateList", date, text);
		}
		if (!date) {
			removeFilter("outStartDateList");
			return;
		}

		setArr.push({key: "outStartDateList", value: date, text});
		setFilter(setArr);
	};

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<Flex direction="column" gap="10">
					<SelectWrap>
						<SelectBox onClick={() => setShow(!show)}>
							<SelectLabel>{monValue}</SelectLabel>
						</SelectBox>
						{show && (
							<SelectOptions>
								<li onClick={handleMonList} value="ALL">
									출발월 전체
								</li>
								{monList &&
									monList.map((item, index) => {
										const text = `${item.substring(0, 4)}년${item.substring(4, 6)}월`;
										return (
											<li onClick={handleMonList} value={item} key={index}>
												{text}
											</li>
										);
									})}
							</SelectOptions>
						)}
					</SelectWrap>
					<style.FilterCalendarWrap>
						<LocalFilterCalendar
							minDate={minDate}
							maxDate={maxDate}
							value={outStartDateValue}
							onDateChange={(date: Dayjs) => {
								if (date === null) {
									removeFilter("outStartDateList");
									return;
								}
								const thisDate = date ? date.format("YYYYMMDD") : "";
								handleCalendar(thisDate);
							}}
						/>
					</style.FilterCalendarWrap>
				</Flex>
			</OptionWrap>
		</>
	);
};

/* 출발요일 */
const StartDay = (prop: FilterType) => {
	const {getFilter, addFilter, setFilter, removeFilter} = useFilterStore();
	const [popOpen] = useOverlay();

	const wdayListData = prop.wdayList?.replaceAll("|", "").split("^");
	const wdayList = wdayListData && ["전체", ...wdayListData];
	const outStartDateValue = getFilter()?.filter(i => i.key === "outStartDateList")[0]?.value || "";

	const handleConfirmAndSetFilter = (key: string, value: string, text: string) => {
		const setArr = getFilter()?.filter(v => v.key !== "outStartDateList");

		const afterConfirm = () => {
			setArr.push({key, value, text});
			setFilter(setArr);
			return false;
		};

		popOpen(props => (
			<Alert
				{...props}
				alertType="confirm"
				text="출발 일정 조건을 변경하시는 경우<br/>기존 설정하신 출발 일정은 초기화 됩니다. <br/>출발 일정 조건을 변경하시겠습니까?"
				afterConfirm={() => typeof afterConfirm === "function" && afterConfirm()}
			/>
		));
		return false;
	};

	const handleWdayList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		const text = `${value}요일 출발`;

		if (value === "전체") return removeFilter("wdayList");

		if (outStartDateValue.length === 8) {
			return handleConfirmAndSetFilter("wdayList", value, text);
		}

		if (checked) addFilter("wdayList", value, text);
		if (!checked) removeFilter("wdayList", value);
	};

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.FilterOptionList>
					{["전체", "월", "화", "수", "목", "금", "토", "일"].map((item, index) =>
						wdayList?.indexOf(item) > -1 ? (
							<li key={index}>
								<input
									type="checkbox"
									id={`wdayList${index}`}
									onChange={handleWdayList}
									value={item}
									checked={
										getFilter()?.some(v => v.value === item) ||
										(getFilter()?.filter(v => v.key === "wdayList").length === 0 && item === "전체")
									}
								/>
								<label htmlFor={`wdayList${index}`}>{item}</label>
							</li>
						) : (
							<li key={index}>
								<span>{item}</span>
							</li>
						),
					)}
				</style.FilterOptionList>
			</OptionWrap>
		</>
	);
};

/* 출발시간 */
const StartTime = (prop: FilterType) => {
	const outDeprtFgListData = prop.outDeprtFgList?.replaceAll("|", "").split("^");
	const outDeprtFgList = outDeprtFgListData && ["전체", ...outDeprtFgListData];

	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handleOutDeprtFgList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		if (value === "전체") return removeFilter("outDeprtFgList");
		checked && addFilter("outDeprtFgList", value, `${value} 출발`);
		!checked && removeFilter("outDeprtFgList", value);
	};
	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.GridListWrap>
					<style.FilterOptionList>
						{outDeprtFgList &&
							outDeprtFgList.map((item, index) => (
								<li key={index}>
									<input
										type="checkbox"
										id={`outDeprtFgList${index}`}
										value={item}
										onChange={handleOutDeprtFgList}
										checked={
											getFilter()?.some(v => v.value === item) ||
											(getFilter()?.filter(v => v.key === "outDeprtFgList").length === 0 && item === "전체")
										}
									/>
									<label htmlFor={`outDeprtFgList${index}`}>{item}</label>
								</li>
							))}
					</style.FilterOptionList>
				</style.GridListWrap>
			</OptionWrap>
		</>
	);
};

/* 출발지(공항) */
const StartAir = (prop: FilterType) => {
	const outCityListData = prop.outCityList?.replaceAll("|", "").split("^");
	const outCityList = outCityListData && ["전체", ...outCityListData];
	const {getFilter, addFilter, removeFilter} = useFilterStore();

	const handleOutCityList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		checked && addFilter("outCityDiviNm", value, `${value} 출발`);
		!checked && removeFilter("outCityDiviNm", value);
	};

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.GridListWrap>
					<style.FilterOptionList>
						{outCityList &&
							outCityList.map((item, index) => (
								<li key={index}>
									<input
										type="checkbox"
										id={`outCityList${index}`}
										value={item}
										onChange={handleOutCityList}
										checked={
											getFilter()?.some(v => v.value === item) ||
											(getFilter()?.filter(v => v.key === "outCityDiviNm").length === 0 && item === "전체")
										}
									/>
									<label htmlFor={`outCityList${index}`}>{item}</label>
								</li>
							))}
					</style.FilterOptionList>
				</style.GridListWrap>
			</OptionWrap>
		</>
	);
};

/* 여행기간 */
const TravelDays = (prop: FilterType) => {
	const evNightListData = prop.evNightList?.replaceAll("|", "").split("^");
	const evNightList = evNightListData && ["전체", ...evNightListData];
	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handleEvNightList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value: text, checked} = e.target;
		if (text === "전체") return removeFilter("evDayCntList");
		const value = e.target?.value?.replace("일", "");
		checked && addFilter("evDayCntList", value, text);
		!checked && removeFilter("evDayCntList", value);
	};
	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.GridListWrap>
					<style.FilterOptionList>
						{evNightList &&
							evNightList.map((item, index) => (
								<li key={index}>
									<input
										type="checkbox"
										id={`evNightList${index}`}
										value={item}
										onChange={handleEvNightList}
										checked={
											getFilter()?.some(v => v.text === item) ||
											(getFilter()?.filter(v => v.key === "evDayCntList").length === 0 && item === "전체")
										}
									/>
									<label htmlFor={`evNightList${index}`}>{item}</label>
								</li>
							))}
					</style.FilterOptionList>
				</style.GridListWrap>
			</OptionWrap>
		</>
	);
};

/* 여행예산(1인 기준) */
const TravelBudget = (prop: FilterType) => {
	const [price, setPrice] = useState({minPrice: "", maxPrice: ""});
	const {getFilter, setFilter} = useFilterStore();

	const keys = ["minPrice", "maxPrice"];
	let setArr = getFilter()?.filter(item => !keys.includes(item.key)) || [];

	const addFilter = () => {
		const minPrice = price.minPrice === "" ? prop.minPrice : price.minPrice.replace(/[^0-9]/g, "");
		const maxPrice = price.maxPrice === "" ? prop.maxPrice : price.maxPrice.replace(/[^0-9]/g, "");

		setArr.push({key: "minPrice", value: minPrice, text: `${format.koKRFormatNumber(minPrice)}원`});
		setArr.push({key: "maxPrice", value: maxPrice, text: `${format.koKRFormatNumber(maxPrice)}원`});
		setFilter(setArr);
	};

	const handleChage = e => {
		const {id, value} = e.target;
		setPrice({...price, [id]: value});
	};

	const handleKeyup = e => {
		const {id, value} = e.target;
		if (e.keyCode === 13) {
			addFilter();
		} else {
			setPrice({...price, [id]: format.koKRFormatNumber(value.replace(/[^0-9]/g, ""))});
		}
	};
	const handleBlur = () => {
		if (price.minPrice.length + price.maxPrice.length > 0) {
			addFilter();
		} else {
			setArr = getFilter().filter(v => !keys.includes(v.key));
			setFilter(setArr);
		}
	};

	useEffect(() => {
		/* 초기값 세팅*/
		const storeMinPrice = getFilter()?.filter(i => i.key === "minPrice")[0]?.value;
		const storeMaxPrice = getFilter()?.filter(i => i.key === "maxPrice")[0]?.value;
		if (storeMinPrice && storeMaxPrice) {
			const minPrice = storeMinPrice === prop.minPrice?.replace(/[^0-9]/g, "") ? "" : storeMinPrice;
			const maxPrice = storeMaxPrice === prop.maxPrice?.replace(/[^0-9]/g, "") ? "" : storeMaxPrice;
			setPrice({...price, minPrice, maxPrice});
		}
	}, [getFilter()]);

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<OptionTextField>
					<input
						type="text"
						id="minPrice"
						value={price.minPrice}
						placeholder={`₩${format.koKRFormatNumber(prop.minPrice)}`}
						onChange={handleChage}
						onKeyUp={handleKeyup}
						onBlur={handleBlur}
					/>
					<span>~</span>
					<input
						type="text"
						id="maxPrice"
						value={price.maxPrice}
						placeholder={`₩${format.koKRFormatNumber(prop.maxPrice)}`}
						onChange={handleChage}
						onKeyUp={handleKeyup}
						onBlur={handleBlur}
					/>
				</OptionTextField>
			</OptionWrap>
		</>
	);
};

/* 이용교통 */
const UseTraffic = (prop: FilterType) => {
	const airListData = prop.airList?.replaceAll("|", "").split("^") || [];
	const airList = [{code: "ALL", name: "전체"}];
	for (let i = 0; i < airListData.length; i += 2) {
		const code = airListData[i];
		const name = airListData[i + 1];
		airList.push({code, name});
	}

	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handleAirList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		if (value === "ALL") return removeFilter("airCd");
		const text = e.target.parentNode.textContent;
		checked && addFilter("airCd", value, text);
		!checked && removeFilter("airCd", value);
	};
	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.FilterOptionList>
					{airList &&
						airList.map((item, index) => (
							<li key={index}>
								<input
									type="checkbox"
									id={`airline-${item.code}`}
									value={item.code}
									onChange={handleAirList}
									checked={
										getFilter()?.some(v => v.value === item.code) ||
										(getFilter()?.filter(v => v.key === "airCd").length === 0 && item.name === "전체")
									}
								/>
								<label htmlFor={`airline-${item.code}`}>{item.name}</label>
							</li>
						))}
				</style.FilterOptionList>
			</OptionWrap>
		</>
	);
};

/* 상품특징 */
const ProductFeatures = (prop: FilterType) => {
	const featuresListData = prop.featuresList.replaceAll("|", "").split("^");
	const featuresList = [{code: "PC00", name: "전체", divi: "PC"}];
	for (let i = 0; i < featuresListData.length; i += 3) {
		const code = featuresListData[i];
		const name = featuresListData[i + 1];
		const divi = featuresListData[i + 2];
		featuresList.push({code, name, divi});
	}

	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handleFeaturesList = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		if (value === "PC00") return removeFilter("goodsFeaturesList");
		const text = e.target.parentNode.textContent;
		checked && addFilter("goodsFeaturesList", value, text);
		!checked && removeFilter("goodsFeaturesList", value);
	};
	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.FilterOptionList>
					{featuresList &&
						featuresList.map((item, index) => (
							<li key={index}>
								<input
									type="checkbox"
									id={`features${item.code}`}
									value={item.code}
									onChange={handleFeaturesList}
									checked={
										getFilter()?.some(v => v.value === item.code) ||
										(getFilter()?.filter(v => v.key === "goodsFeaturesList").length === 0 && item.name === "전체")
									}
								/>
								<label htmlFor={`features${item.code}`}>{item.name}</label>
							</li>
						))}
				</style.FilterOptionList>
			</OptionWrap>
		</>
	);
};

/* 프로모션 */
const Promotion = (prop: FilterType) => {
	const promotionListData = prop.promotionList.replaceAll("|", "").split("^");
	const promotionList = [{code: "ALL", name: "전체"}];
	for (let i = 0; i < promotionListData.length; i += 2) {
		const code = promotionListData[i];
		const name = promotionListData[i + 1];
		promotionList.push({code, name});
	}

	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handlePromotionList = e => {
		const {value, checked} = e.target;
		if (value === "ALL") return removeFilter("goodsPromotion");
		const text = e.target.parentNode.innerText;
		checked && addFilter("goodsPromotion", value, text);
		!checked && removeFilter("goodsPromotion", value);
	};

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.FilterOptionList>
					{promotionList &&
						promotionList.map((item, index) => (
							<li key={index}>
								<input
									type="checkbox"
									id={`promotion${item.code}`}
									value={item.code}
									onChange={handlePromotionList}
									checked={
										getFilter()?.some(v => v.value === item.code) ||
										(getFilter()?.filter(v => v.key === "goodsPromotion").length === 0 && item.name === "전체")
									}
								/>
								<label htmlFor={`promotion${item.code}`}>{item.name}</label>
							</li>
						))}
				</style.FilterOptionList>
			</OptionWrap>
		</>
	);
};

/* 여행구성원 */
const TravelMember = (prop: FilterType) => {
	const tmemberListData = prop.tmemberList.replaceAll("|", "").split("^");
	const tmemberList = [];
	for (let i = 0; i < tmemberListData.length; i += 2) {
		const code = tmemberListData[i];
		const name = tmemberListData[i + 1];
		tmemberList.push({code, name});
	}

	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const handleTmemberList = e => {
		const {value, checked} = e.target;
		const text = e.target.parentNode.innerText;
		checked && addFilter("goodsTmember", value, text);
		!checked && removeFilter("goodsTmember", value);
	};

	return (
		<>
			<OptionWrap show={prop.active && "show"}>
				<style.FilterOptionList>
					{tmemberList &&
						tmemberList.map((item, index) => (
							<li key={index}>
								<input
									type="checkbox"
									id={`tmemberList${item.code}`}
									value={item.code}
									onChange={handleTmemberList}
									checked={getFilter()?.some(v => v.value === item.code) || false}
								/>
								<label htmlFor={`tmemberList${item.code}`}>{item.name}</label>
							</li>
						))}
				</style.FilterOptionList>
			</OptionWrap>
		</>
	);
};

/* 상품 검색*/
const GoodsNm = () => {
	const goodsNmRef = useRef(null);
	const {getFilter, setFilter} = useFilterStore();

	const addFilter = (value: string) => {
		const setArr = getFilter()?.filter(v => v.key !== "goodsNm");
		value && setArr.push({key: "goodsNm", value, text: value});
		setFilter(setArr);
	};
	const handleKeyup = e => {
		if (e.keyCode === 13) {
			const {value} = e.target;
			addFilter(value);
		}
	};

	const handleClick = () => addFilter(goodsNmRef.current.value);
	return (
		<SearchWrap>
			<Search>
				<input
					type="text"
					id="goodsNm"
					placeholder="상품 상세 검색"
					onKeyUp={handleKeyup}
					ref={goodsNmRef}
					defaultValue={getFilter()?.filter(v => v.key === "goodsNm")[0]?.text}
				/>
				<button type="submit" onClick={handleClick}>
					<span>검색</span>
				</button>
			</Search>
		</SearchWrap>
	);
};

const Banner = ({item}: {item: BannerContents}) => {
	const [isError, setIsError] = useState(false);
	const imageOnError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
		setIsError(true);
	};

	const {taggingClick} = useTaggingClick("pkg_select_promo_locallist", {...item, selectDiv: "localListFilter"});

	return (
		<style.BannerImg>
			<a
				href={`${publicRuntimeConfig.ENV_PACKAGE_URL}${item.link}` || "#"}
				target={getTarget(item.target)}
				onClick={() => taggingClick()}
			>
				<img
					src={!isError ? `${publicRuntimeConfig.ENV_CIMG_CDN_URL}${item.imgPath}` : errorImg}
					width={240}
					height={140}
					alt={item.alt}
					onError={imageOnError}
				/>
			</a>
		</style.BannerImg>
	);
};

/* 프로모션 배너 */
const BannerPromotion = () => {
	const bannerPromotionFetch = useBannerContents("GOLI");
	const bannerPromotion = bannerPromotionFetch.data?.body;

	return <>{bannerPromotion && bannerPromotion.map((item, index) => <Banner key={index} item={item} />)}</>;
};

const LocalSideFilterComponent = (filter: GoodsFilter) => {
	const [active, setActive] = useState<number | null>(0);

	const getFilterList = useMemo(() => {
		/* 제휴 분기처리 작업 필요 */
		const arr = [];
		arr.push({title: "출발 일정", contents: StartDate});
		arr.push({title: "출발 요일", contents: StartDay});
		arr.push({title: "출발 시간", contents: StartTime});
		arr.push({title: "출발지(공항)", contents: StartAir});
		arr.push({title: "여행기간", contents: TravelDays});
		arr.push({title: "여행예산(1인 기준)", contents: TravelBudget});
		arr.push({title: "이용교통", contents: UseTraffic});
		filter.featuresList && arr.push({title: "상품특징", contents: ProductFeatures});
		filter.promotionList && arr.push({title: "프로모션", contents: Promotion});
		filter.tmemberList && arr.push({title: "여행구성원", contents: TravelMember});

		return arr;
	}, [filter]);

	return (
		<>
			<GoodsNm />
			{getFilterList.length > 0 &&
				getFilterList.map((item, index) => {
					const Contents = item.contents;
					return (
						<div key={index}>
							<OptionTitle
								onClick={() => setActive(value => (value === index ? null : index))}
								active={active === index}
							>
								{item.title}
							</OptionTitle>
							<Contents {...filter} active={active === index} />
						</div>
					);
				})}
			<BannerPromotion />
		</>
	);
};

/* 제휴 분기처리 작업 필요 */
const LocalSideFilter = (props: GoodsFilter) => (
	<ErrorBoundary logMsg="SideFilter Error">
		<LocalSideFilterComponent {...props} />
	</ErrorBoundary>
);

export default LocalSideFilter;
